import { useEffect, useState, type ReactElement } from "react";
import { Label } from "../../../../shared/components/ui/label";
import { useImageUpload } from "../../hooks/useImageUpload";
import { FileUploadZone } from "./FileUploadZone";
import { ImagePreview } from "./ImagePreview";
import { ImageSourceToggle } from "./ImageSourceToggle";
import { UploadMethodToggle } from "./UploadMethodToggle";
import { URLInputField } from "./URLInputField";

interface ImageUploadFieldProps {
  submissionId: string | null;
  label: string;
  value: string | null;
  onChange: (url: string) => void;
  type: "poster" | "backdrop" | "logo" | "horizontal-tile" | "vertical-tile";
  recommendedSize?: string;
  aspectRatio?: string;
  mode?: "submission" | "title"; // Which entity type we're editing
  // Image source metadata (only for title mode)
  imageSource?: string;
  tmdbUrl?: string | null;
  r2Url?: string | null;
  onToggle?: () => void;
  // Persisted validation warnings from ImageAsset metadata
  persistedWarnings?: string[];
}

/**
 * Image Upload Field Component
 * Supports file upload to R2 or URL input for images
 */
// eslint-disable-next-line max-lines-per-function
export function ImageUploadField({
  submissionId,
  label,
  value,
  onChange,
  type,
  recommendedSize,
  aspectRatio,
  mode = "submission",
  imageSource,
  tmdbUrl,
  r2Url,
  onToggle,
  persistedWarnings,
}: Readonly<ImageUploadFieldProps>): ReactElement {
  const [uploadMethod, setUploadMethod] = useState<"url" | "file">("url");
  const [urlInput, setUrlInput] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<string[]>(persistedWarnings || []);
  const { uploadImage, uploading } = useImageUpload();

  // Sync validation warnings when persistedWarnings prop changes
  useEffect(() => {
    if (persistedWarnings) {
      setValidationWarnings(persistedWarnings);
    }
  }, [persistedWarnings]);

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) return;

    try {
      new URL(urlInput);
      onChange(urlInput);
      setUrlInput("");
      setError(null);
    } catch {
      setError("Please enter a valid URL");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be smaller than 5MB");
      return;
    }

    if (!submissionId) {
      setError("Please save a draft before uploading images");
      return;
    }

    setError(null);

    try {
      // Create blob URL for immediate preview
      const blobUrl = URL.createObjectURL(file);
      setPreviewUrl(blobUrl);

      // Upload to R2 and get R2 key
      const result = await uploadImage({
        ...(mode === "title" ? { titleId: submissionId || undefined } : { submissionId: submissionId || undefined }),
        file,
        imageType: type,
        mode,
      });

      // Check for validation errors (should block upload)
      if (result.validation && !result.validation.valid) {
        setError(`Image validation failed: ${result.validation.errors.join(", ")}`);
        URL.revokeObjectURL(blobUrl);
        setPreviewUrl(null);
        return;
      }

      // Show warnings but don't block upload
      if (result.validation && result.validation.warnings.length > 0) {
        setValidationWarnings(result.validation.warnings);
        // Auto-clear warnings after 10 seconds
        setTimeout(() => setValidationWarnings([]), 10000);
      }

      // Store the R2 key in the form (format: "r2://bucket/key")
      // This allows us to fetch presigned read URLs later
      onChange(`r2://${result.key}`);

      // Clean up blob URL after successful upload
      URL.revokeObjectURL(blobUrl);
      setPreviewUrl(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      // Clean up preview on error
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  };

  const handleRemove = () => {
    onChange("");
    setUrlInput("");
    setError(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label>{label}</Label>
          {recommendedSize && (
            <p className="text-xs text-neutral-500 mt-1">
              Recommended: {recommendedSize}
              {aspectRatio && ` (${aspectRatio})`}
            </p>
          )}
        </div>
        <UploadMethodToggle uploadMethod={uploadMethod} onMethodChange={setUploadMethod} />
      </div>

      {value && (
        <>
          <ImagePreview
            value={value}
            label={label}
            type={type}
            submissionId={submissionId}
            onRemove={handleRemove}
            mode={mode}
            validationWarnings={validationWarnings}
          />
          {/* Show toggle for title edit mode when we have alternate versions */}
          {mode === "title" && submissionId && imageSource && (tmdbUrl || r2Url) && onToggle && (
            <ImageSourceToggle
              titleId={submissionId}
              imageType={type}
              currentSource={imageSource}
              tmdbUrl={tmdbUrl ?? null}
              r2Url={r2Url ?? null}
              currentUrl={value}
              onToggle={onToggle}
            />
          )}
        </>
      )}

      {!value && (
        <div className="space-y-2">
          {uploadMethod === "url" ? (
            <URLInputField
              value={urlInput}
              onChange={setUrlInput}
              onSubmit={handleUrlSubmit}
              onClearError={() => setError(null)}
            />
          ) : (
            <FileUploadZone type={type} uploading={uploading} onFileChange={handleFileUpload} />
          )}
          {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
          {validationWarnings.length > 0 && (
            <div className="mt-2 p-3 bg-yellow-900/20 border border-yellow-700/30 rounded-md">
              <p className="text-sm text-yellow-400 font-medium mb-1">⚠️ Image Quality Warnings:</p>
              <ul className="text-xs text-yellow-300 space-y-1 list-disc list-inside">
                {validationWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
